// FLUX PATCH — "Save to Flux": serialize the (hand-edited) splat and POST it back to
// the local Flux Studio server, which converts to SOG IN THE BACKGROUND and overwrites
// the scene's splat. No superspl.at login, no manual export. The server accepts
// immediately (202) and we poll its job endpoint to confirm — the editor never hangs.
// Scene id comes from the ?fluxScene URL param (set by the Studio "Refine in Editor").
import { Events } from './events';
import { serializePlyCompressed } from './splat-serialize';

// In-memory FileSystem: collects the serializer's output into a single buffer.
class MemFileSystem {
    chunks: Uint8Array[] = [];
    total = 0;

    createWriter(_filename: string) {
        const self = this;
        return {
            get bytesWritten() {
                return self.total;
            },
            write: (data: Uint8Array) => {
                self.chunks.push(data.slice());
                self.total += data.byteLength;
            },
            close: () => Promise.resolve()
        };
    }

    mkdir(_path: string) {
        return Promise.resolve();
    }

    bytes(): Uint8Array {
        const out = new Uint8Array(this.total);
        let o = 0;
        for (const c of this.chunks) {
            out.set(c, o);
            o += c.byteLength;
        }
        return out;
    }
}

// Poll the background conversion job and pop a final confirmation. Runs detached.
const pollJob = async (events: Events, studio: string, id: string) => {
    const url = `${studio}/api/scene/${encodeURIComponent(id)}/job`;
    const deadline = Date.now() + 15 * 60 * 1000;
    while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 4000));
        let s: any;
        try {
            s = await fetch(url).then(r => r.json());
        } catch {
            continue;
        }
        if (s.status === 'done') {
            await events.invoke('showPopup', {
                type: 'info',
                header: 'Saved to Flux',
                message: 'Encoding finished — your cleaned splat is live. Refresh the Flux viewer to see it.'
            });
            return;
        }
        if (s.status === 'error') {
            await events.invoke('showPopup', {
                type: 'error',
                header: 'Save to Flux failed',
                message: s.error || 'conversion error'
            });
            return;
        }
    }
};

const registerFluxSave = (events: Events) => {
    events.function('flux.save', async () => {
        const params = new URL(location.href).searchParams;
        const id = params.get('fluxScene');
        const studio = params.get('fluxStudio') || 'http://localhost:4000';

        if (!id) {
            await events.invoke('showPopup', {
                type: 'error',
                header: 'Save to Flux',
                message: 'This splat wasn\'t opened from Flux. Use "Refine in Editor" on a scene in the Flux Studio dashboard.'
            });
            return;
        }

        const splats = events.invoke('scene.splats');
        if (!splats || splats.length === 0) {
            await events.invoke('showPopup', { type: 'error', header: 'Save to Flux', message: 'Nothing to save.' });
            return;
        }

        events.fire('startSpinner');
        try {
            // serialize to compressed PLY in memory (single file, splat-transform-readable)
            const fs = new MemFileSystem();
            await serializePlyCompressed(splats, { minOpacity: 1 / 255, removeInvalid: true }, fs);

            const res = await fetch(`${studio}/api/scene/${encodeURIComponent(id)}/splat?format=compressed.ply`, {
                method: 'POST',
                body: new Blob([fs.bytes()]),
                headers: { 'Content-Type': 'application/octet-stream' }
            });

            // server returns 202 immediately and converts in the background
            if (!res.ok) {
                const e = await res.json().catch(() => ({}));
                throw new Error(e.error || `save failed (${res.status})`);
            }

            events.fire('stopSpinner'); // upload accepted — don't block on the ~minute encode
            await events.invoke('showPopup', {
                type: 'info',
                header: 'Saving to Flux',
                message: 'Uploaded — encoding in the background. Keep working; I\'ll confirm when it\'s live (then refresh the Flux viewer).'
            });

            void pollJob(events, studio, id); // detached; confirms when done
        } catch (error) {
            events.fire('stopSpinner');
            await events.invoke('showPopup', {
                type: 'error',
                header: 'Save to Flux failed',
                message: `${(error as Error).message ?? error}`
            });
        }
    });
};

export { registerFluxSave };
